import {
  CALL_QUALITY_TIERS,
  MAX_ACTIVE_PEERS,
  SCREEN_MAX_BITRATE,
  gainForDistance,
  type PairMediaState,
  type SignalPayload,
  type TrackKind,
} from '@etown/shared';
import type { AppSocket } from '../net/socketClient';
import { useMediaStore, type RingState } from '../stores/mediaStore';
import { localMedia } from './localMedia';
import { PeerSession } from './PeerSession';
import { spatialAudio } from './spatialAudio';

/** Wie ein Peer im Raum steht — Grundlage fuer Lautstaerke und Panorama. */
export interface PeerAudioCue {
  distanceTiles: number;
  /** -1 links, 0 mittig, +1 rechts. */
  pan: number;
  /** true = eine Wand dazwischen; klingt gedaempft statt abgeschnitten. */
  occluded: boolean;
}

/** Restlautstaerke hinter einer Wand. */
const OCCLUDED_GAIN_FACTOR = 0.35;

interface ManagedPeer {
  session: PeerSession;
  displayName: string;
  connection: 'connecting' | 'connected' | 'failed';
  /** false = pending-Paar: Verbindung steht (warm), aber keine Medien. */
  mediaActive: boolean;
  ring: RingState;
  streams: Partial<Record<TrackKind, MediaStream>>;
}

/**
 * Orchestriert alle PeerSessions: Der Server meldet proximity:enter/leave,
 * hierauf werden Verbindungen auf- bzw. abgebaut. Medien fliessen nur zu
 * Peers mit Freigabe (mediaActive — kein Anklingeln noetig, Zone oder
 * angenommener Klingelversuch). Lokale Track-Aenderungen werden an alle
 * freigegebenen Sessions verteilt; eingehende Streams landen im mediaStore
 * (React) und im Spatial-Audio-Graph.
 */
export class RtcManager {
  private peers = new Map<string, ManagedPeer>();
  private socket: AppSocket | null = null;
  private iceServers: RTCIceServer[] = [];
  private unsubscribeMedia: (() => void) | null = null;

  /**
   * MUSS vor room:join laufen — proximity:enter kann sonst eintreffen,
   * bevor der Manager bereit ist, und wuerde still verworfen.
   * Idempotent (Reconnect ruft erneut auf).
   */
  init(socket: AppSocket, iceServers: RTCIceServer[]): void {
    this.socket = socket;
    this.iceServers = iceServers;
    // Lokale Tracks an alle freigegebenen Verbindungen verteilen
    this.unsubscribeMedia?.();
    this.unsubscribeMedia = localMedia.onTrackChange((kind, track) => {
      for (const peer of this.peers.values()) {
        if (peer.mediaActive) void peer.session.setTrack(kind, track);
      }
      if (track && (kind === 'cam' || kind === 'screen')) this.applyQualityTiers();
    });
  }

  /** Server: Peer ist in Reichweite -> Verbindung aufbauen (auch pending). */
  onProximityEnter(payload: {
    peerId: string;
    displayName: string;
    initiator: boolean;
    media: PairMediaState;
  }): void {
    if (this.peers.has(payload.peerId) || !this.socket) return;
    if (this.peers.size >= MAX_ACTIVE_PEERS) {
      // Bekannte Mesh-Grenze: Server deckelt Accepts zusaetzlich
      return;
    }
    spatialAudio.resume();

    // polite = die Seite, die bei Glare zurueckrollt (deterministisch)
    const polite = !payload.initiator;
    const session = new PeerSession(payload.peerId, polite, this.iceServers, {
      sendSignal: (signal: SignalPayload) =>
        this.socket?.emit('webrtc:signal', { to: payload.peerId, payload: signal }),
      sendTrackMeta: (mid, kind) =>
        this.socket?.emit('webrtc:track-meta', { to: payload.peerId, mid, kind }),
      onRemoteStream: (kind, stream) => this.handleRemoteStream(payload.peerId, kind, stream),
      onConnectionState: (state) => this.updatePeer(payload.peerId, { connection: state }),
    });

    this.peers.set(payload.peerId, {
      session,
      displayName: payload.displayName,
      connection: 'connecting',
      mediaActive: payload.media === 'active',
      ring: 'none',
      streams: {},
    });
    this.publishPeer(payload.peerId);
    this.updateCaptureState();

    // Vorhandene lokale Tracks nur bei Freigabe sofort anbieten
    if (payload.media === 'active') {
      this.offerLocalTracks(session);
      this.applyQualityTiers();
    }
  }

  /** Server: Peer ausser Reichweite -> Verbindung abbauen. */
  onProximityLeave(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.session.close();
    spatialAudio.detach(peerId);
    this.peers.delete(peerId);
    useMediaStore.getState().removePeer(peerId);
    this.updateCaptureState();
    this.applyQualityTiers();
  }

  /** Server: Medien-Freigabe fuer ein Paar (Annahme, Zone, Settings). */
  onCallAccepted(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer || peer.mediaActive) {
      this.updatePeer(peerId, { ring: 'none' });
      return;
    }
    peer.mediaActive = true;
    peer.ring = 'none';
    this.publishPeer(peerId);
    this.offerLocalTracks(peer.session);
    this.updateCaptureState();
    this.applyQualityTiers();
  }

  setRingState(peerId: string, ring: RingState): void {
    this.updatePeer(peerId, { ring });
  }

  /** Server: ein Paar wurde aufgelegt — Medien beenden, Verbindung bleibt warm. */
  onCallEnded(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer || !peer.mediaActive) return;
    peer.mediaActive = false;
    peer.ring = 'none';
    // Eigene Tracks aus der Session nehmen und Remote-Streams abraeumen
    for (const kind of ['mic', 'cam', 'screen'] as const) void peer.session.setTrack(kind, null);
    spatialAudio.detach(peerId);
    peer.streams = {};
    this.publishPeer(peerId);
    this.updateCaptureState();
    this.applyQualityTiers();
  }

  handleSignal(from: string, payload: SignalPayload): void {
    void this.peers.get(from)?.session.handleSignal(payload);
  }

  handleTrackMeta(from: string, mid: string, kind: TrackKind): void {
    this.peers.get(from)?.session.handleTrackMeta(mid, kind);
  }

  /**
   * Raum-Update aus dem Spiel -> Lautstaerke, Stereo-Position und Daempfung
   * des Peers. Die Distanzkurve bleibt unveraendert; hinter einer Wand kommt
   * nur ein Faktor obendrauf.
   */
  setPeerAudio(peerId: string, cue: PeerAudioCue): void {
    const gain = gainForDistance(cue.distanceTiles) * (cue.occluded ? OCCLUDED_GAIN_FACTOR : 1);
    spatialAudio.setGain(peerId, gain);
    spatialAudio.setPan(peerId, cue.pan);
  }

  connectedPeerIds(): string[] {
    return [...this.peers.keys()];
  }

  /** Peers mit aktivem Mikrofon-Stream (Sprech-Ring am Avatar). */
  speakingPeerIds(): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const [id, peer] of this.peers) {
      if (peer.streams.mic) ids.add(id);
    }
    return ids;
  }

  destroy(): void {
    for (const [peerId, peer] of this.peers) {
      peer.session.close();
      spatialAudio.detach(peerId);
    }
    this.peers.clear();
    this.unsubscribeMedia?.();
    spatialAudio.detachAll();
    localMedia.setCaptureAllowed(false);
    useMediaStore.getState().reset();
  }

  /** Alle vorhandenen lokalen Tracks an eine Session geben (Verhandlung startet). */
  private offerLocalTracks(session: PeerSession): void {
    for (const kind of ['mic', 'cam', 'screen'] as const) {
      const track = localMedia.getTrack(kind);
      if (track) void session.setTrack(kind, track);
    }
  }

  /** Capture nur, solange mindestens ein Peer Medien-Freigabe hat. */
  private updateCaptureState(): void {
    const anyActive = [...this.peers.values()].some((peer) => peer.mediaActive);
    localMedia.setCaptureAllowed(anyActive);
    if (!anyActive && localMedia.getTrack('screen')) localMedia.stopScreen();
  }

  /**
   * Sende-Qualitaet an die Gespraechsgroesse anpassen (Mesh: Upload waechst
   * linear mit der Peer-Zahl) — Tier aus CALL_QUALITY_TIERS pro Sender.
   */
  private applyQualityTiers(): void {
    const activePeers = [...this.peers.values()].filter((peer) => peer.mediaActive);
    const tier =
      CALL_QUALITY_TIERS.find((t) => activePeers.length <= t.maxPeers) ??
      CALL_QUALITY_TIERS[CALL_QUALITY_TIERS.length - 1]!;
    for (const peer of activePeers) {
      void peer.session.setVideoQuality('cam', {
        scaleResolutionDownBy: tier.scaleResolutionDownBy,
        maxBitrate: tier.maxBitrate,
        maxFramerate: tier.maxFramerate,
      });
      void peer.session.setVideoQuality('screen', { maxBitrate: SCREEN_MAX_BITRATE });
    }
  }

  private handleRemoteStream(peerId: string, kind: TrackKind, stream: MediaStream | null): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    // Schutzschicht: von pending-Peers duerfen keine Medien ankommen —
    // konforme Clients senden nichts, andere werden hier verworfen
    if (stream && !peer.mediaActive) return;
    if (stream) peer.streams = { ...peer.streams, [kind]: stream };
    else {
      const { [kind]: _removed, ...rest } = peer.streams;
      peer.streams = rest;
    }
    if (kind === 'mic') {
      if (stream) spatialAudio.attach(peerId, stream);
      else spatialAudio.detach(peerId);
    }
    this.publishPeer(peerId);
  }

  private updatePeer(peerId: string, patch: Partial<Pick<ManagedPeer, 'connection' | 'ring'>>): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    Object.assign(peer, patch);
    this.publishPeer(peerId);
  }

  private publishPeer(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    useMediaStore.getState().upsertPeer({
      peerId,
      displayName: peer.displayName,
      connection: peer.connection,
      mediaActive: peer.mediaActive,
      ring: peer.ring,
      streams: peer.streams,
    });
  }
}
